// R116 #3 (Jesse: "I think we need to do the same now for the readiness
// guide. It's pretty cluttered and not particularly easy on the eyes." —
// the Calendar stay panel as the design direction): the PURE rules behind the
// redesigned Property Readiness Guide (client/src/pages/OnboardingPage.ts →
// drawReadinessGuide). Shared by the client (ESM import) and
// `node lib/readinessGuide.test.mjs`. Nothing here reads or writes data.
//
//   sectionPlan(counts)        which page sections show, in what order, and
//                              which start open ("don't bury the content":
//                              the sections that HOLD the home's items lead)
//   splitRooms(rooms, countOf) checklist rooms with items first, empty rooms
//                              after (both keep the inspectors' walk order)
//   twinRoomOf(area, rooms)    an unfiled area's near-duplicate checklist room
//                              ("kitchen" → Kitchen, "Bedroom 1 Suite" →
//                              Bedroom 1, "Living room" → Living)
//   promoteKind(area, twin)    the kind "Make it an area" gives a new room — a
//                              twin never adds a second bedroom/bathroom (that
//                              would raise the bed/bath count banner)
//   roomPills(facts)           ≤ 2 pills for a room card, most useful first;
//                              "No reference" only when SOME room has one
//   urlsIn(text) / isLinkCell  captions that carry links → Links & documents
//   isUnnamed / nextPhotoN     bulk-added "Photo N" items (the naming step)
//   itemsWord(n)               "1 item" / "3 items" (the migrate banner counts
//                              ITEMS — a cell may hold no photo at all)

/** "1 item" / "2 items". */
export function itemsWord(n, one = 'item', many = 'items') {
  const k = Number(n) || 0;
  return `${k} ${k === 1 ? one : many}`;
}

// http(s) links inside free text; trailing sentence punctuation is not part of the link.
const URL_RE = /\bhttps?:\/\/[^\s<>"'`]+/gi;
/** Unique http(s) URLs in a caption, in order of appearance. */
export function urlsIn(text) {
  const out = [];
  for (const m of String(text == null ? '' : text).match(URL_RE) || []) {
    const u = m.replace(/[.,;:!?)\]}]+$/, '');
    if (u.length > 8 && !out.includes(u)) out.push(u);
  }
  return out;
}

/** A whole-property cell whose caption carries a link (guidebook, listing links, a walkthrough video…). */
export function isLinkCell(cell) {
  return !!cell && typeof cell === 'object' && urlsIn(cell.value).length > 0;
}

const PHOTO_N = /^Photo (\d+)$/;
/** A bulk-added item that still carries its placeholder name. */
export function isUnnamed(label) { return PHOTO_N.test(String(label == null ? '' : label).trim()); }

/** The next free "Photo N" number past every existing one. */
export function nextPhotoN(labels) {
  let max = 0;
  for (const l of Array.isArray(labels) ? labels : []) {
    const m = PHOTO_N.exec(String(l == null ? '' : l).trim());
    if (m) max = Math.max(max, Number(m[1]));
  }
  return max + 1;
}

const norm = (s) => String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * The checklist room an unfiled area most likely means, or null.
 *   'same'   — equal ignoring case / punctuation / spacing ("kitchen", "Living-Room")
 *   'prefix' — a room label is a whole-word prefix of the area ("Bedroom 1 Suite" →
 *              Bedroom 1, "Bathroom 3 Primary" → Bathroom 3, "Living room" → Living);
 *              the LONGEST such label wins, and "Bedroom 10" is never "Bedroom 1".
 * rooms: [{ key, label }].
 */
export function twinRoomOf(area, rooms) {
  const a = norm(area);
  if (!a) return null;
  const list = (Array.isArray(rooms) ? rooms : []).filter(r => r && r.key != null && norm(r.label));
  const same = list.find(r => norm(r.label) === a);
  if (same) return { room: same, why: 'same' };
  let best = null;
  for (const r of list) {
    const l = norm(r.label);
    if (a.startsWith(l + ' ') && (!best || l.length > norm(best.label).length)) best = r;
  }
  return best ? { room: best, why: 'prefix' } : null;
}

/** The kind "Make it an area" uses: a twin of a checklist room is 'other' (no second bedroom / bathroom). */
export function promoteKind(area, twin) {
  if (twin) return 'other';
  const l = String(area == null ? '' : area);
  if (/bed/i.test(l)) return 'bedroom';
  if (/bath/i.test(l)) return 'bathroom';
  if (/kitchen/i.test(l)) return 'kitchen';
  if (/living|family|den/i.test(l)) return 'living';
  if (/exterior|patio|yard|garage|deck|pool|front|back|outside/i.test(l)) return 'exterior';
  return 'other';
}

/**
 * Rooms with items first, empty ones after; both keep the checklist (walk) order.
 * countOf(room) → the room's item count. Each entry: { room, index (walk position), n }.
 */
export function splitRooms(rooms, countOf) {
  const full = [], empty = [];
  (Array.isArray(rooms) ? rooms : []).forEach((room, index) => {
    const n = Math.max(0, Number(countOf(room)) || 0);
    (n > 0 ? full : empty).push({ room, index, n });
  });
  return { full, empty };
}

/**
 * The page's sections in display order. counts = { rooms, unfiled, whole, links }
 * (items in checklist rooms, items in unfiled areas, whole-property items, link items).
 * Sections that hold items come first in the default order (rooms, unfiled, whole,
 * links); the rest follow. Unfiled and Links are hidden while empty; Whole property
 * always shows (its "＋ Add" lives there). A section with items starts OPEN; the
 * rooms list is always open. PAR inventory is not part of this plan (always last,
 * collapsed by default).
 * Returns [{ key, n, open, shown }] (shown:false entries are dropped).
 */
export function sectionPlan(counts) {
  const c = counts || {};
  const n = (k) => Math.max(0, Number(c[k]) || 0);
  const defs = [
    { key: 'rooms', n: n('rooms'), always: true },
    { key: 'unfiled', n: n('unfiled'), always: false },
    { key: 'whole', n: n('whole'), always: true },
    { key: 'links', n: n('links'), always: false },
  ];
  const shown = defs.filter(d => d.always || d.n > 0);
  const ordered = [...shown.filter(d => d.n > 0), ...shown.filter(d => d.n === 0)];
  const out = ordered.map(d => ({ key: d.key, n: d.n, open: d.key === 'rooms' ? true : d.n > 0 }));
  // R117: the PROPERTY CHECKLIST (owner/property info, owner rules, checklist
  // items) always shows, open, right after the rooms — never sorted away.
  if ('checklist' in c) out.splice(out.findIndex(d => d.key === 'rooms') + 1, 0, { key: 'checklist', n: n('checklist'), open: true });
  return out;
}

/**
 * ≤ 2 pills for a room card, most useful first.
 * facts = { orphan, legacy (cells still in Reference Photos), unnamed ("Photo N"
 * items), refs (this room's reference photos), anyRefs (does ANY room have one) }.
 * Returns [{ text, tone, title }]. tone ∈ accent | warn | none.
 */
export function roomPills(facts) {
  const f = facts || {};
  const out = [];
  if (f.orphan) out.push({ text: 'Removed from checklist', tone: 'none', title: 'These items belong to a room that is no longer on the photo checklist — move them to a room from each item\'s ⋯.' });
  if (Number(f.legacy) > 0) out.push({ text: `${itemsWord(f.legacy)} to move`, tone: 'accent', title: 'Still in the old Reference Photos list under this room\'s name — open the room and use "Move into the guide".' });
  if (Number(f.unnamed) > 0) out.push({ text: `${f.unnamed} unnamed`, tone: 'warn', title: 'Bulk-added photos still called "Photo N" — a name reads better on cleaners\' job sheets.' });
  if (!f.orphan && !(Number(f.refs) > 0) && f.anyRefs) out.push({ text: 'No reference', tone: 'none', title: 'No reference photo yet — inspectors use it to know which room is which.' });
  return out.slice(0, 2);
}
